import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Play, Lock, CheckCircle, Users, Clock, Star, Shield, AlertCircle, Clock3, CreditCard, FileText } from 'lucide-react';
import api from '../utils/api';
import useAuthStore from '../store/authStore';
import toast from 'react-hot-toast';

// ===== 防录屏：动态水印（含学员身份 + 实时时间，录屏可溯源）=====
const maskPhoneLite = (p) => (typeof p === 'string' && /^\d{11}$/.test(p) ? p.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2') : (p || ''));
const VideoWatermark = ({ user }) => {
  const [tick, setTick] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 20000);
    return () => clearInterval(t);
  }, []);
  const raw = (user && (user.phone || user.name || user.username || user.email)) || '';
  const idText = maskPhoneLite(raw) || '游客';
  const stamp = idText + '  ' + new Date().toLocaleString('zh-CN', { hour12: false });
  const positions = [
    { top: '7%', left: '4%' },
    { top: '7%', left: '58%' },
    { top: '80%', left: '4%' },
    { top: '80%', left: '58%' },
    { top: '42%', left: '30%' }
  ];
  const drift = positions[tick % positions.length];
  const base = {
    position: 'absolute', color: 'rgba(255,255,255,0.20)', fontSize: '12px',
    textShadow: '0 1px 3px rgba(0,0,0,0.55)', whiteSpace: 'nowrap',
    userSelect: 'none', transform: 'rotate(-8deg)', transition: 'all 1.5s ease',
    pointerEvents: 'none'
  };
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 20 }} aria-hidden="true">
      <div style={{ ...base, top: '7%', left: '4%' }}>{stamp}</div>
      <div style={{ ...base, top: '7%', left: '58%' }}>{stamp}</div>
      <div style={{ ...base, top: '80%', left: '4%' }}>{stamp}</div>
      <div style={{ ...base, top: '80%', left: '58%' }}>{stamp}</div>
      <div style={{ ...base, top: drift.top, left: drift.left, color: 'rgba(255,255,255,0.30)', fontSize: '14px' }}>{stamp}</div>
    </div>
  );
};

const CourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeLesson, setActiveLesson] = useState(null);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [videoSrc, setVideoSrc] = useState(''); // 视频播放地址
  const [videoLoading, setVideoLoading] = useState(false);

  // 购买引导：跳转添加老师微信（由用户指定，勿改）
  const WECHAT_ID = 'Xiaoshuang338827';
  // 弹窗与申请表单状态
  const [showWechat, setShowWechat] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applyName, setApplyName] = useState('');
  const [applyPhone, setApplyPhone] = useState('');
  const [applyNote, setApplyNote] = useState('');
  const [applyFormError, setApplyFormError] = useState('');

  useEffect(() => {
    fetchCourse();
  }, [id]);

  // 当 activeLesson 变化时，获取视频播放地址
  useEffect(() => {
    if (!activeLesson?.videoUrl) {
      setVideoSrc('');
      return;
    }
    // 免费试看：同样走后端令牌通道（COS 桶已私有化，直链已不可用）
    if (activeLesson.isFree) {
      if (isAuthenticated) {
        fetchVideoToken();
      } else {
        setVideoSrc('');
      }
      return;
    }
    // 需要购买的视频：通过 token 鉴权播放
    if (canWatch) {
      fetchVideoToken();
    }
  }, [activeLesson, course?.isEnrolled]);

  // 获取视频播放 token
  const fetchVideoToken = async () => {
    if (!activeLesson || !course) return;
    setVideoLoading(true);
    try {
      const deviceId = localStorage.getItem('deviceId') || '';
      const data = await api.post('/videos/token', {
        courseId: course._id,
        lessonId: activeLesson._id,
        deviceId
      });
      if (data.success) {
        setVideoSrc(`/api/videos/stream/${data.data.token}`);
      } else {
        setVideoSrc('');
        toast.error(data.message || '获取播放地址失败');
      }
    } catch (error) {
      setVideoSrc('');
      toast.error(error.message || '获取播放地址失败');
    } finally {
      setVideoLoading(false);
    }
  };

  // 拦截「保存网页 / 打印」快捷键（配合水印形成威慑）
  useEffect(() => {
    const onKey = (e) => {
      const k = (e.key || '').toLowerCase();
      if ((e.ctrlKey || e.metaKey) && (k === 's' || k === 'p')) e.preventDefault();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (isAuthenticated && course) {
      checkApplicationStatus();
    }
  }, [isAuthenticated, course?._id]);

  const fetchCourse = async () => {
    try {
      const data = await api.get(`/courses/${id}`);
      if (data.success) {
        setCourse(data.data);
        // 兼容 sections 和 lessons 两种结构
        const allLessons = [];
        if (data.data.sections && data.data.sections.length > 0) {
          data.data.sections.forEach(s => {
            if (s.lessons) allLessons.push(...s.lessons);
          });
        } else if (data.data.lessons && data.data.lessons.length > 0) {
          allLessons.push(...data.data.lessons);
        }
        if (allLessons.length > 0) {
          setActiveLesson(allLessons[0]);
        }
      }
    } catch (error) {
      toast.error('获取课程失败');
    } finally {
      setLoading(false);
    }
  };

  const checkApplicationStatus = async () => {
    try {
      const data = await api.get(`/authorizations/check/${id}`);
      if (data.success) {
        setApplicationStatus(data.data);
      }
    } catch (error) {
      // 静默处理
    }
  };

  const handleApplyForAccess = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    setApplyFormError('');
    setApplyName('');
    setApplyPhone('');
    setApplyNote('');
    setShowApplyModal(true);
  };

  const submitApplication = async () => {
    if (!applyName.trim()) {
      setApplyFormError('请填写申请人姓名');
      return;
    }
    if (!/^1[3-9]\d{9}$/.test(applyPhone.trim())) {
      setApplyFormError('请填写正确的手机号');
      return;
    }
    setApplyLoading(true);
    try {
      const data = await api.post(`/authorizations/courses/${id}/apply`, {
        name: applyName.trim(),
        phone: applyPhone.trim(),
        note: applyNote.trim()
      });
      if (data.success) {
        toast.success('申请已提交，等待审核');
        setShowApplyModal(false);
        checkApplicationStatus();
      }
    } catch (error) {
      toast.error(error.message || '申请失败');
    } finally {
      setApplyLoading(false);
    }
  };

  const handlePurchase = async (type) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    setPurchaseLoading(true);
    try {
      if (type === 'group') {
        const data = await api.post('/group-buys/create', { courseId: id });
        if (data.success) {
          toast.success('团购创建成功！');
          navigate(`/group-buy/${data.data._id}`);
        }
      } else {
        // 个人购买：直接引导添加老师微信，不再走平台下单（避免产生未付款脏订单）
        setShowWechat(true);
      }
    } catch (error) {
      toast.error(error.message || '操作失败');
    } finally {
      setPurchaseLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
      </div>
    );
  }

  if (!course) {
    return <div className="text-center py-12 text-gray-500">课程不存在</div>;
  }

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('zh-CN');
  };

  // 授权课程的状态栏
  const renderAuthorizedStatus = () => {
    if (course.accessType !== 'authorized') return null;
    if (course.isEnrolled) {
      const expired = course.enrollment?.expiresAt && new Date(course.enrollment.expiresAt) < new Date();
      return (
        <div className={`border rounded-lg p-4 flex items-center ${expired ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
          {expired ? (
            <>
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-red-700">
                授权已于 {formatDate(course.enrollment?.expiresAt)} 到期，
                <button onClick={handleApplyForAccess} className="underline font-medium ml-1">重新申请</button>
              </span>
            </>
          ) : (
            <>
              <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
              <span className="text-green-700">
                已授权 · 有效期至 {formatDate(course.enrollment?.expiresAt)} · 学习进度：{course.enrollment?.progress || 0}%
              </span>
            </>
          )}
        </div>
      );
    }

    if (!isAuthenticated) {
      return (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center">
          <Shield className="h-5 w-5 text-amber-500 mr-2" />
          <span className="text-amber-700">此课程需要授权观看，请先
            <button onClick={() => navigate('/login')} className="underline font-medium mx-1">登录</button>
            后申请
          </span>
        </div>
      );
    }

    // 已登录但未申请
    if (!applicationStatus?.hasApplied) {
      return (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <Shield className="h-5 w-5 text-blue-500 mr-2" />
            <span className="text-blue-700 font-medium">此课程为授权观看</span>
          </div>
          <p className="text-blue-600 text-sm mb-3">提交申请后，管理员审核通过即可开始学习。默认授权时长 {course.defaultAccessDays || 30} 天。</p>
          <button
            onClick={handleApplyForAccess}
            disabled={applyLoading}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {applyLoading ? '提交中...' : '申请观看'}
          </button>
        </div>
      );
    }

    // 申请中
    if (applicationStatus.status === 'pending') {
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center">
          <Clock3 className="h-5 w-5 text-yellow-500 mr-2" />
          <span className="text-yellow-700">
            审核中 · 提交于 {formatDate(applicationStatus.createdAt)}，请耐心等待管理员审核
          </span>
        </div>
      );
    }

    // 被拒绝
    if (applicationStatus.status === 'rejected') {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700 font-medium">申请未通过</span>
          </div>
          {applicationStatus.reviewNote && (
            <p className="text-red-600 text-sm mb-3">原因：{applicationStatus.reviewNote}</p>
          )}
          <button
            onClick={handleApplyForAccess}
            disabled={applyLoading}
            className="bg-red-600 text-white px-4 py-1.5 rounded text-sm hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            重新申请
          </button>
        </div>
      );
    }

    // 已过期
    if (applicationStatus.status === 'expired') {
      return (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-gray-500 mr-2" />
          <span className="text-gray-700">
            授权已过期，
            <button onClick={handleApplyForAccess} className="underline font-medium ml-1 text-blue-600">重新申请</button>
          </span>
        </div>
      );
    }

    return null;
  };

  const canWatch = course.isEnrolled || course.accessType === 'public' || applicationStatus?.status === 'approved';

  // 收集所有课时（兼容 sections + lessons 两种结构）
  const allLessons = [];
  if (course.sections && course.sections.length > 0) {
    course.sections.forEach(s => {
      if (s.lessons) allLessons.push(...s.lessons);
    });
  } else if (course.lessons && course.lessons.length > 0) {
    allLessons.push(...course.lessons);
  }

  return (
    <div className="space-y-6">
      {/* 课程头部 */}
      <div className="card overflow-hidden">
        <div className="aspect-video bg-gray-900 relative">
          {videoSrc ? (
            <div className="relative w-full h-full">
              <video
                key={videoSrc}
                src={videoSrc}
                controls
                className="w-full h-full"
                poster={course.thumbnail}
                controlsList="nodownload noplaybackrate noremoteplayback"
                disablePictureInPicture
                disableRemotePlayback
                onContextMenu={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
              />
              <VideoWatermark user={user} />
            </div>
          ) : activeLesson?.videoUrl && (canWatch || activeLesson.isFree) ? (
            <div className="w-full h-full flex flex-col items-center justify-center bg-gray-800 text-white">
              {videoLoading ? (
                <>
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-2"></div>
                  <p>获取播放地址中...</p>
                </>
              ) : (
                <>
                  <Play className="h-16 w-16 mb-4 opacity-50" />
                  <p className="text-lg">点击课时开始学习</p>
                </>
              )}
            </div>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900 text-white">
              <Play className="h-16 w-16 mb-4 opacity-50" />
              <p className="text-lg">
                {canWatch ? '选择课时开始学习' :
                   course.accessType === 'authorized' ? '获得授权后可观看完整课程' : '购买后可观看完整课程'}
              </p>
            </div>
          )}
        </div>
        <div className="p-6">
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="bg-primary-100 text-primary-700 px-3 py-1 rounded-full text-sm">{course.category}</span>
            {course.accessType === 'authorized' && (
              <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm flex items-center">
                <Shield className="h-3 w-3 mr-1" />
                授权观看
              </span>
            )}
            <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm flex items-center">
              <Star className="h-3 w-3 mr-1 text-yellow-400 fill-current" />
              {course.stats?.rating?.toFixed(1) || '5.0'}
            </span>
            <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm flex items-center">
              <Users className="h-3 w-3 mr-1" />
              {course.stats?.totalStudents || 0} 人在学
            </span>
          </div>
          <h1 className="text-2xl font-bold mb-4">{course.title}</h1>
          <p className="text-gray-600 mb-6">{course.description}</p>
          
          {/* 已登录且未购买/未申请：显示双选项 */}
          {isAuthenticated && !course.isEnrolled && (!applicationStatus || !applicationStatus.hasApplied) && (
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-700">选择观看方式</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {/* 直接购买 */}
                <div className="border-2 border-primary-200 rounded-xl p-5 hover:border-primary-400 transition-colors bg-gradient-to-br from-primary-50 to-white">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="h-5 w-5 text-primary-600" />
                    <span className="font-bold text-primary-700">加微信购买观看</span>
                  </div>
                  <p className="text-3xl font-bold text-primary-600 mb-2">¥999</p>
                  <p className="text-sm text-gray-500 mb-4">添加老师微信，付费后解锁全部课程内容</p>
                  <button
                    onClick={() => handlePurchase('individual')}
                    disabled={purchaseLoading}
                    className="w-full bg-primary-600 text-white px-4 py-2.5 rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
                  >
                    {purchaseLoading ? '处理中...' : '加微信购买'}
                  </button>
                </div>

                {/* 内部申请 */}
                <div className="border-2 border-amber-200 rounded-xl p-5 hover:border-amber-400 transition-colors bg-gradient-to-br from-amber-50 to-white">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="h-5 w-5 text-amber-600" />
                    <span className="font-bold text-amber-700">内部申请观看</span>
                  </div>
                  <p className="text-2xl font-bold text-amber-600 mb-2">免费申请</p>
                  <p className="text-sm text-gray-500 mb-4">提交申请，讲师审核通过后即可观看</p>
                  <button
                    onClick={handleApplyForAccess}
                    disabled={applyLoading}
                    className="w-full bg-amber-500 text-white px-4 py-2.5 rounded-lg font-medium hover:bg-amber-600 transition-colors disabled:opacity-50"
                  >
                    {applyLoading ? '提交中...' : '申请观看'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 申请中状态 */}
          {applicationStatus?.hasApplied && applicationStatus.status === 'pending' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center mt-4">
              <Clock3 className="h-5 w-5 text-yellow-500 mr-2" />
              <span className="text-yellow-700">
                审核中 · 提交于 {formatDate(applicationStatus.createdAt)}，请耐心等待讲师审核
              </span>
            </div>
          )}

          {/* 申请已通过 */}
          {applicationStatus?.hasApplied && applicationStatus.status === 'approved' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center mt-4">
              <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
              <span className="text-green-700">
                已授权 · 有效期至 {formatDate(applicationStatus.expiresAt)} · 学习进度：{course.enrollment?.progress || 0}%
              </span>
            </div>
          )}

          {/* 申请被拒绝 */}
          {applicationStatus?.hasApplied && applicationStatus.status === 'rejected' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
              <div className="flex items-center mb-2">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                <span className="text-red-700 font-medium">申请未通过</span>
              </div>
              {applicationStatus.reviewNote && (
                <p className="text-red-600 text-sm mb-3">原因：{applicationStatus.reviewNote}</p>
              )}
              <button
                onClick={handleApplyForAccess}
                disabled={applyLoading}
                className="bg-red-600 text-white px-4 py-1.5 rounded text-sm hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                重新申请
              </button>
            </div>
          )}

          {/* 已购买或已授权（但applicationStatus未加载时显示） */}
          {course.isEnrolled && !applicationStatus?.hasApplied && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center mt-4">
              <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
              <span className="text-green-700">
                {course.enrollment?.accessType === 'authorized'
                  ? `已授权 · 有效期至 ${formatDate(course.enrollment?.expiresAt)} · 学习进度：${course.enrollment?.progress || 0}%`
                  : '您已购买此课程'}
              </span>
            </div>
          )}

          {/* 未登录 */}
          {!isAuthenticated && (
            <div className="space-y-3 mt-4">
              <h3 className="font-semibold text-gray-700">选择观看方式</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="border-2 border-gray-200 rounded-xl p-5 bg-gradient-to-br from-primary-50 to-white">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="h-5 w-5 text-primary-600" />
                    <span className="font-bold text-primary-700">加微信购买观看</span>
                  </div>
                  <p className="text-3xl font-bold text-primary-600 mb-2">¥999</p>
                  <p className="text-sm text-gray-500 mb-4">添加老师微信，付费后解锁全部课程内容</p>
                  <button
                    onClick={() => setShowWechat(true)}
                    className="w-full bg-primary-600 text-white px-4 py-2.5 rounded-lg font-medium hover:bg-primary-700 transition-colors"
                  >
                    加微信购买
                  </button>
                </div>
                <div className="border-2 border-gray-200 rounded-xl p-5 bg-gradient-to-br from-amber-50 to-white">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="h-5 w-5 text-amber-600" />
                    <span className="font-bold text-amber-700">内部申请观看</span>
                  </div>
                  <p className="text-2xl font-bold text-amber-600 mb-2">免费申请</p>
                  <p className="text-sm text-gray-500 mb-4">提交申请，讲师审核通过后即可观看</p>
                  <button
                    onClick={() => navigate('/login')}
                    className="w-full bg-amber-500 text-white px-4 py-2.5 rounded-lg font-medium hover:bg-amber-600 transition-colors"
                  >
                    登录后申请
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 旧的授权课程状态（保留兼容） */}
          {!applicationStatus && renderAuthorizedStatus()}
        </div>
      </div>

      {/* 课时列表 */}
      <div className="card p-6">
        <h2 className="text-xl font-bold mb-4">课程目录</h2>
        <div className="space-y-2">
          {allLessons.map((lesson, index) => (
            <div
              key={lesson._id}
              onClick={() => setActiveLesson(lesson)}
              className={`flex items-center p-4 rounded-lg cursor-pointer transition-colors ${
                activeLesson?._id === lesson._id ? 'bg-primary-50 border border-primary-200' : 'hover:bg-gray-50 border border-transparent'
              }`}
            >
              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-medium text-gray-600 mr-4">
                {index + 1}
              </div>
              <div className="flex-1">
                <h3 className="font-medium">{lesson.title}</h3>
                <p className="text-sm text-gray-500 flex items-center mt-1">
                  <Clock className="h-3 w-3 mr-1" />
                  {Math.floor((lesson.duration || 0) / 60)}:{String((lesson.duration || 0) % 60).padStart(2, '0')}
                  {lesson.isFree && <span className="ml-2 text-primary-600">免费试看</span>}
                </p>
              </div>
              {(canWatch || lesson.isFree) ? (
                <Play className="h-5 w-5 text-primary-600" />
              ) : (
                <Lock className="h-5 w-5 text-gray-400" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 微信引导弹窗（购买引导：添加老师微信） */}
      {showWechat && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowWechat(false)}
        >
          <div
            className="bg-white rounded-xl p-6 max-w-sm w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold mb-2">添加老师微信购买课程</h3>
            <p className="text-gray-600 text-sm mb-4">
              本课程通过老师微信购买，请添加下方微信号，备注课程名称即可，老师会为你开通学习权限。
            </p>
            <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-3 mb-4">
              <span className="text-lg font-mono font-bold flex-1 select-all">{WECHAT_ID}</span>
              <button
                onClick={() => navigator.clipboard && navigator.clipboard.writeText(WECHAT_ID)}
                className="bg-primary-600 text-white px-3 py-1.5 rounded text-sm hover:bg-primary-700 transition-colors"
              >
                复制
              </button>
            </div>
            <button
              onClick={() => setShowWechat(false)}
              className="w-full bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition-colors"
            >
              关闭
            </button>
          </div>
        </div>
      )}

      {/* 免费申请弹窗（姓名 + 手机号必填） */}
      {showApplyModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowApplyModal(false)}
        >
          <div
            className="bg-white rounded-xl p-6 max-w-sm w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold mb-4">申请免费观看</h3>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              姓名 <span className="text-red-500">*</span>
            </label>
            <input
              value={applyName}
              onChange={(e) => setApplyName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-3 focus:outline-none focus:ring-2 focus:ring-amber-400"
              placeholder="请输入真实姓名"
            />
            <label className="block text-sm font-medium text-gray-700 mb-1">
              手机号 <span className="text-red-500">*</span>
            </label>
            <input
              value={applyPhone}
              onChange={(e) => setApplyPhone(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-3 focus:outline-none focus:ring-2 focus:ring-amber-400"
              placeholder="请输入手机号"
              inputMode="numeric"
              maxLength={11}
            />
            <label className="block text-sm font-medium text-gray-700 mb-1">申请理由（选填）</label>
            <textarea
              value={applyNote}
              onChange={(e) => setApplyNote(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-3 focus:outline-none focus:ring-2 focus:ring-amber-400"
              rows={2}
              placeholder="选填"
            />
            {applyFormError && <p className="text-red-500 text-sm mb-2">{applyFormError}</p>}
            <div className="flex gap-2">
              <button
                onClick={() => setShowApplyModal(false)}
                className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition-colors"
              >
                取消
              </button>
              <button
                onClick={submitApplication}
                disabled={applyLoading}
                className="flex-1 bg-amber-500 text-white py-2 rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50"
              >
                {applyLoading ? '提交中...' : '提交申请'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseDetail;
